import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@nepal-ecom/ui';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Optional grouping label — items with the same group are shown under one header. */
  group?: string;
  /** Optional indent level (e.g. for parent/child categories). */
  depth?: number;
  /** Optional searchable extras (e.g. parent name, synonyms). */
  keywords?: string[];
}

interface Props {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  /** Optional dropdown max-height in pixels (default 320). */
  maxHeight?: number;
}

/**
 * Lightweight searchable combobox. No external deps — closes on outside click,
 * Escape, or selection. Arrow keys + Enter work inside the search box.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches',
  disabled,
  loading,
  clearable = false,
  maxHeight = 320,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    // Focus search on open
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      if (o.label.toLowerCase().includes(q)) return true;
      if (o.group?.toLowerCase().includes(q)) return true;
      return o.keywords?.some((k) => k.toLowerCase().includes(q));
    });
  }, [options, query]);

  // Group for rendering
  const grouped = useMemo(() => {
    const out: Array<{ group: string | null; items: ComboboxOption[] }> = [];
    for (const opt of filtered) {
      const g = opt.group ?? null;
      const last = out[out.length - 1];
      if (last && last.group === g) last.items.push(opt);
      else out.push({ group: g, items: [opt] });
    }
    return out;
  }, [filtered]);

  // Reset active index when query changes
  useEffect(() => { setActiveIdx(0); }, [query]);

  const selectAt = (idx: number) => {
    const opt = filtered[idx];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectAt(activeIdx);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={cn(
          'h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-left text-sm shadow-xs',
          'focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500',
          'flex items-center justify-between gap-2',
          'disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed',
        )}
      >
        <span className={cn('truncate', !selected && 'text-slate-400')}>
          {loading ? 'Loading…' : selected ? selected.label : placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="text-slate-400 hover:text-slate-700 p-0.5 rounded"
              aria-label="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && (
        <div
          className="absolute z-30 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden"
        >
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder={searchPlaceholder}
                className="w-full h-8 pl-8 pr-2 text-sm bg-slate-50 rounded-md border border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
              />
            </div>
          </div>

          <div
            className="overflow-y-auto py-1"
            style={{ maxHeight }}
          >
            {filtered.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-slate-500">{emptyText}</p>
            ) : (
              grouped.map((g, gi) => {
                const items = g.items.map((opt) => {
                  const idx = filtered.indexOf(opt);
                  const isActive = idx === activeIdx;
                  const isSelected = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => selectAt(idx)}
                      onMouseEnter={() => setActiveIdx(idx)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors',
                        isActive ? 'bg-primary-50 text-primary-800' : 'text-slate-700 hover:bg-slate-50',
                      )}
                      style={{ paddingLeft: 12 + (opt.depth ?? 0) * 16 }}
                    >
                      <Check
                        className={cn(
                          'h-3.5 w-3.5 flex-shrink-0',
                          isSelected ? 'text-primary-600' : 'text-transparent',
                        )}
                      />
                      <span className="truncate">{opt.label}</span>
                    </button>
                  );
                });
                return (
                  <div key={g.group ?? `g-${gi}`}>
                    {g.group && (
                      <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                        {g.group}
                      </p>
                    )}
                    {items}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
